import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faFilter, faSearch, faSnowplow } from '@fortawesome/free-solid-svg-icons';

const AdminPeripheralStatusReportPage = () => {
    const [farms, setFarms] = useState([]);
    const [selectedFarm, setSelectedFarm] = useState('');
    const [ponds, setPonds] = useState([]);
    const [selectedPond, setSelectedPond] = useState('');
    const [peripherals, setPeripherals] = useState([]);
    const [selectedPeripheral, setSelectedPeripheral] = useState('');
    const [peripheralData, setPeripheralData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    
    const startOfcurrentDate = new Date();
    const endOfCurrentDate = new Date();
    startOfcurrentDate.setHours(0, 0, 0, 0);
    endOfCurrentDate.setHours(23, 59, 59, 999);
    
    const [startDate, setStartDate] = useState(startOfcurrentDate);
    const [endDate, setEndDate] = useState(endOfCurrentDate);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [pagination, setPagination] = useState({
        total: 0,
        currentPage: 1,
        totalPages: 1,
    });

    const [pageSize, setPageSize] = useState(50);
    const [itemsPerPageOptions] = useState([10, 25, 50, 100, 200]);
    const { t } = useTranslation();
    const [districts, setDistricts] = useState([]);
    const [ngos, setNgos] = useState([]);
    const [filterNgo, setFilterNgo] = useState('');
    const [filterDistrict, setFilterDistrict] = useState('');
    const [filterSubdistrict, setFilterSubdistrict] = useState('');
    const [filterSubdistricts, setFilterSubdistricts] = useState([]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/farm/all/list`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => {
            setFarms(response.data.farms);
        }).catch((error) => {
            Swal.fire({
                icon: 'error',
                title: t('failed_to_fetch_farms') || 'Failed to fetch farms',
                text: error.response?.data?.message || 'An error occurred while fetching farms.',
            });
        });
    }, [t]);

    useEffect(() => {
        setSelectedFarm('');
    }, [filterDistrict, filterSubdistrict]);

    useEffect(() => {
        const fetchDistricts = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/districts`);
                setDistricts(response.data);
            } catch (err) {
                console.error('Failed to fetch districts', err);
            }
        };
        const fetchNgos = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/ngos`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setNgos(response.data);
            } catch (error) {
                console.error('Failed to fetch NGOs:', error);
            }
        };
        fetchNgos();
        fetchDistricts();
    }, []);

    const fetchPeripheralData = (pondId = null, page = 1, recurring = false) => {
        if (!pondId) {
            Swal.fire({
                icon: 'warning',
                title: 'Please select a pond',
                text: 'You must select a pond to view peripheral status reports.',
            });
            return;
        }

        const token = localStorage.getItem('token');
        setLoading(true);
        let query = `?page=${page}&limit=${pageSize}`;
        if (startDate) query += `&startDate=${startDate.toISOString()}`;
        if (endDate) query += `&endDate=${endDate.toISOString()}`;
        if (selectedPeripheral) query += `&peripheralId=${selectedPeripheral}`;

        axios.get(`${process.env.REACT_APP_BASE_URL}/api/peripherals/pond/${pondId}/status-report${query}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => {
            let dataToShow = response.data.data || response.data;
            
            if (recurring) {
                setPeripheralData((prevData) => [...prevData, ...dataToShow]);
            } else {
                setPeripheralData(dataToShow);
            }
            setPagination({
                total: response.data.pagination?.totalItems || response.data.pagination?.total || dataToShow.length,
                currentPage: response.data.pagination?.currentPage || 1,
                totalPages: response.data.pagination?.totalPages || 1,
            });
            setLoading(false);
        }).catch((error) => {
            Swal.fire({
                icon: 'error',
                title: 'Failed to fetch peripheral status report',
                text: error.response?.data?.message || 'An error occurred while fetching peripheral status report.',
            });
            setLoading(false);
        });
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.totalPages && page !== pagination.currentPage) {
            fetchPeripheralData(selectedPond, page);
        }
    };

    const getPaginationNumbers = () => {
        const pages = [];
        const current = pagination.currentPage;
        const total = pagination.totalPages;
        
        if (current > 3) pages.push(1);
        if (current > 4) pages.push('...');
        
        for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) {
            pages.push(i);
        }
        
        if (current < total - 3) pages.push('...');
        if (current < total - 2) pages.push(total);
        
        return pages;
    };

    const fetchPonds = (farmId) => {
        const token = localStorage.getItem('token');
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/pond/farm/${farmId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => {
            setPonds(response.data);
        }).catch((error) => {
            Swal.fire({
                icon: 'error',
                title: 'Failed to fetch ponds',
                text: error.response?.data?.message || 'An error occurred while fetching ponds.',
            });
        });
    };

    const fetchPeripherals = (pondId) => {
        const token = localStorage.getItem('token');
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/peripherals/pond/${pondId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => {
            setPeripherals(response.data);
        }).catch((error) => {
            Swal.fire({
                icon: 'error',
                title: 'Failed to fetch peripherals',
                text: error.response?.data?.message || 'An error occurred while fetching peripherals.',
            });
        });
    };

    const handleFarmChange = (e) => {
        const farmId = e.target.value;
        setSelectedFarm(farmId);
        setSelectedPond('');
        setSelectedPeripheral('');
        setPonds([]);
        setPeripherals([]);
        setPeripheralData([]);
        if (farmId) {
            fetchPonds(farmId);
        }
    };

    const handlePondChange = (e) => {
        const pondId = e.target.value;
        setSelectedPond(pondId);
        setSelectedPeripheral('');
        setPeripherals([]);
        setPeripheralData([]);
        if (pondId) {
            fetchPeripherals(pondId);
        }
    };

    const handlePeripheralChange = (e) => {
        const peripheralId = e.target.value;
        setSelectedPeripheral(peripheralId);
        setPeripheralData([]);
    };

    const handleDateFilter = () => {
        fetchPeripheralData(selectedPond, 1);
    };

    const resetFilters = () => {
        const today = new Date();
        const startOfToday = new Date(today);
        const endOfToday = new Date(today);
        startOfToday.setHours(0, 0, 0, 0);
        endOfToday.setHours(23, 59, 59, 999);
        
        setStartDate(startOfToday);
        setEndDate(endOfToday);
        setSelectedMonth(today.getMonth());
        setSelectedYear(today.getFullYear());
        setFilterNgo('');
        setFilterDistrict('');
        setFilterSubdistrict('');
        setFilterSubdistricts([]);
        setSelectedFarm('');
        setSelectedPond('');
        setPonds([]);
        setPeripheralData([]);
        setPagination({
            total: 0,
            currentPage: 1,
            totalPages: 1,
        });
    };

    const getMonthNames = () => {
        return [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
    };

    const getYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear - 5; year <= currentYear + 1; year++) {
            years.push(year);
        }
        return years;
    };

    const setDatePreset = (preset) => {
        const now = new Date();
        let start, end;
        
        switch (preset) {
            case 'today':
                start = new Date(now);
                end = new Date(now);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'yesterday':
                start = new Date(now);
                end = new Date(now);
                start.setDate(start.getDate() - 1);
                end.setDate(end.getDate() - 1);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'week':
                start = new Date(now);
                end = new Date(now);
                start.setDate(start.getDate() - 7);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'month':
                start = new Date(now);
                end = new Date(now);
                start.setDate(start.getDate() - 30);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            default:
                return;
        }
        
        setStartDate(start);
        setEndDate(end);
        
        fetchPeripheralData(selectedPond, 1);
    };

    const handleMonthChange = (monthNumber) => {
        setSelectedMonth(monthNumber);
        const startOfMonth = new Date(selectedYear, monthNumber, 1, 0, 0, 0, 0);
        const endOfMonth = new Date(selectedYear, monthNumber + 1, 0, 23, 59, 59, 999);

        setStartDate(startOfMonth);
        setEndDate(endOfMonth);
    };

    const handleYearChange = (yearNumber) => {
        setSelectedYear(yearNumber);
        const startOfMonth = new Date(yearNumber, selectedMonth, 1, 0, 0, 0, 0);
        const endOfMonth = new Date(yearNumber, selectedMonth + 1, 0, 23, 59, 59, 999);

        setStartDate(startOfMonth);
        setEndDate(endOfMonth);
    };

    useEffect(() => {
        let filtered = peripheralData.filter(item =>
            item.peripheral_id?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.date?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.on_count?.toString().includes(searchTerm) ||
            item.off_count?.toString().includes(searchTerm) ||
            item.total_duration?.toString().includes(searchTerm)
        );

        filtered.sort((a, b) => {
            let aVal, bVal;
            
            if (sortField === 'name') {
                aVal = a.peripheral_id?.name?.toLowerCase() || '';
                bVal = b.peripheral_id?.name?.toLowerCase() || '';
            } else if (sortField === 'date') {
                aVal = new Date(a.date);
                bVal = new Date(b.date);
            } else if (sortField === 'total_duration' || sortField === 'on_count' || sortField === 'off_count') {
                aVal = parseFloat(a[sortField]) || 0;
                bVal = parseFloat(b[sortField]) || 0;
            } else {
                aVal = a[sortField] || '';
                bVal = b[sortField] || '';
            }
            
            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

        setFilteredData(filtered);
    }, [peripheralData, searchTerm, sortField, sortDirection]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const exportToCSV = () => {
        if (filteredData.length === 0) {
            Swal.fire('No Data', 'No data available to export', 'info');
            return;
        }

        const csvContent = [
            ['Peripheral Name', 'Date', 'Total Duration (hours)', 'On Count', 'Off Count', 'First Activity', 'Last Activity'],
            ...filteredData.map(item => [
                item.peripheral_id?.name || '',
                new Date(item.date).toLocaleDateString(),
                (item.total_duration / 3600).toFixed(1),
                item.on_count || 0,
                item.off_count || 0,
                item.first_activity ? new Date(item.first_activity).toLocaleString() : '',
                item.last_activity ? new Date(item.last_activity).toLocaleString() : ''
            ])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `peripheral-status-report-${new Date().getTime()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="container mx-auto p-6 bg-gray-50 min-h-screen">
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <FontAwesomeIcon icon={faSnowplow} className="text-blue-600" />
                        {t('peripheral_status_report') || 'Peripheral Status Report'}
                    </h1>
                </div>
            </div>

            {/* District and NGO Filters */}
            <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faFilter} className="text-gray-600" />
                    {t('location_organization_filters') || 'Location and Organization Filters'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('Filter by District') || 'Filter by District'}
                        </label>
                        <select
                            value={filterDistrict}
                            onChange={(e) => {
                                const selected = e.target.value;
                                setFilterDistrict(selected);
                                const found = districts.find(d => d.name === selected);
                                setFilterSubdistricts(found ? found.subdistricts : []);
                                setFilterSubdistrict('');
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">{t('All') || 'All'} {t('Districts') || 'Districts'}</option>
                            {districts.map((d) => (
                                <option key={d._id} value={d.name}>{t(d.name)}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('Filter by Subdistrict') || 'Filter by Subdistrict'}
                        </label>
                        <select
                            value={filterSubdistrict}
                            onChange={(e) => setFilterSubdistrict(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            disabled={!filterDistrict}
                        >
                            <option value="">{t('All') || 'All'} {t('Subdistricts') || 'Subdistricts'}</option>
                            {filterSubdistricts.map((s, i) => (
                                <option key={i} value={s}>{t(s)}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('Filter by NGO') || 'Filter by NGO'}
                        </label>
                        <select
                            value={filterNgo}
                            onChange={(e) => {
                                setFilterNgo(e.target.value);
                                setSelectedFarm('');
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">{t('All') || 'All'} {t('NGOs') || 'NGOs'}</option>
                            {ngos.map((ngo) => (
                                <option key={ngo._id} value={ngo._id}>{ngo.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-3 mb-6">
                <h2 className="text-md font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <FontAwesomeIcon icon={faFilter} className="text-gray-600" />
                    {t('filters') || 'Filters'}
                </h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    <div className="space-y-3 bg-blue-100 rounded-lg shadow-lg p-3">
                        <div className="p-2 bg-blue-50 rounded border border-blue-200">
                            <label className="block text-l font-medium text-gray-700 mb-1">
                                {t('select_farm') || 'Select Farm'} <span className="text-l text-red-500">*</span>
                            </label>
                            <select
                                value={selectedFarm}
                                onChange={handleFarmChange}
                                className="w-full h-10 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-l"
                            >
                                <option value="">{t('select_farm') || 'Select Farm'}</option>
                                {farms
                                    .filter((farm) => {
                                        const matchNgo = filterNgo ? farm.ngo_id === filterNgo : true;
                                        const matchDistrict = filterDistrict ? farm.district === filterDistrict : true;
                                        const matchSubdistrict = filterSubdistrict ? farm.subdistrict === filterSubdistrict : true;
                                        return matchNgo && matchDistrict && matchSubdistrict;
                                    })
                                    .map((farm) => (
                                    <option key={farm._id} value={farm._id}>
                                        🏞️ {farm.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedFarm && (
                            <div className="p-2 bg-blue-50 rounded border border-blue-200">
                                <label className="block text-l font-medium text-gray-700 mb-1">
                                    {t('select_pond') || 'Select Pond'} <span className="text-l text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedPond}
                                    onChange={handlePondChange}
                                    className="w-full h-10 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-l"
                                >
                                    <option value="">{t('select_pond') || 'Select Pond'}</option>
                                    {ponds.map((pond) => (
                                        <option key={pond._id} value={pond._id}>
                                            🏞️ {pond.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {selectedPond && (
                            <div className="p-2 bg-blue-50 rounded border border-blue-200">
                                <label className="block text-l font-medium text-gray-700 mb-1">
                                    {t('select_peripheral') || 'Select Peripheral'}
                                </label>
                                <select
                                    value={selectedPeripheral}
                                    onChange={handlePeripheralChange}
                                    className="w-full h-10 px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-l"
                                >
                                    <option value="">{t('select_peripheral') || 'Select Peripheral'}</option>
                                    {peripherals.map((peripheral) => (
                                        <option key={peripheral._id} value={peripheral._id}>
                                            🔌 {peripheral.name} (TID: {peripheral.tid})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="p-2 bg-blue-50 rounded border border-blue-200">
                            <label className="block text-l font-medium text-gray-700 mb-1">{t('quick_date_presets')}</label>
                            <div className="grid grid-cols-2 gap-2 mb-4">
                                <button
                                    onClick={() => setDatePreset('today')}
                                    className="px-2 py-1 text-l bg-green-200 hover:bg-gray-200 text-gray-700 rounded shadow-lg transition-colors"
                                >
                                    {t('today') || 'Today'}
                                </button>
                                <button
                                    onClick={() => setDatePreset('yesterday')}
                                    className="px-2 py-1 text-l bg-green-200 hover:bg-gray-200 text-gray-700 rounded shadow-lg transition-colors"
                                >
                                    {t('yesterday') || 'Yesterday'}
                                </button>
                                <button
                                    onClick={() => setDatePreset('week')}
                                    className="px-2 py-1 text-l bg-green-200 hover:bg-gray-200 text-gray-700 rounded shadow-lg transition-colors"
                                >
                                    {t('seven_days') || '7 Days'}
                                </button>
                                <button
                                    onClick={() => setDatePreset('month')}
                                    className="px-2 py-1 text-l bg-green-200 hover:bg-gray-200 text-gray-700 rounded shadow-lg transition-colors"
                                >
                                    {t('thirty_days') || '30 Days'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3 bg-blue-100 rounded-lg shadow-lg p-3">
                        <div className="p-2 bg-blue-50 rounded border border-blue-200">
                            <label className="block text-l font-medium text-blue-900 mb-1">{t('select_month_year')}</label>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                <select
                                    value={selectedMonth}
                                    onChange={(e) => handleMonthChange(Number(e.target.value))}
                                    className="h-10 px-1 py-1 border border-blue-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white text-l"
                                >
                                    {getMonthNames().map((month, index) => (
                                        <option key={index} value={index}>
                                            {month.substring(0, 3)}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={selectedYear}
                                    onChange={(e) => handleYearChange(Number(e.target.value))}
                                    className="h-10 px-1 py-1 border border-blue-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white text-l"
                                >
                                    {getYearOptions().map((year) => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="p-2 bg-blue-50 rounded border border-blue-200">
                            <label className="block text-l font-medium text-blue-900 mb-1">{t('select_date_range')}</label>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                <DatePicker
                                    selected={startDate}
                                    onChange={setStartDate}
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    dateFormat="MM/dd HH:mm"
                                    className="h-10 w-full px-2 py-2 border border-blue-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white text-l"
                                    placeholderText="From date & time"
                                />
                                <DatePicker
                                    selected={endDate}
                                    onChange={setEndDate}
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    dateFormat="MM/dd HH:mm"
                                    className="h-10 w-full px-2 py-2 border border-blue-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white text-l"
                                    placeholderText="To date & time"
                                />
                            </div>
                        </div>

                        <div className="space-y-3">


                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={handleDateFilter}
                                    disabled={loading}
                                    className={`
                                    w-full flex items-center justify-center py-3 px-4 rounded-xl text-white font-medium
                                    transition-all duration-300 ease-in-out transform
                                    ${loading
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 hover:-translate-y-0.5 shadow-lg hover:shadow-xl'
                                    }
                                `}
                                >
                                    <FontAwesomeIcon icon={faSearch} className="mr-2 text-lg" />
                                    {loading ? (
                                        <span className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Loading...
                                    </span>
                                    ) : t('apply_filters')}
                                </button>
                                <button
                                    onClick={resetFilters}
                                    disabled={loading}
                                    className={`
                                        flex items-center justify-center py-2.5 px-4 rounded-xl font-medium
                                        transition-all duration-300 ease-in-out
                                        ${loading
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300 hover:shadow-md active:scale-95'
                                    }
                                    `}
                                >
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                                    </svg>
                                    {t('reset_filters')}
                                </button>

                                {/*<a*/}
                                {/*    href="/water-quality-table"*/}
                                {/*    className="flex items-center justify-center py-2.5 px-4 rounded-xl font-medium text-white*/}
                                {/*       bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700*/}
                                {/*       transition-all duration-300 ease-in-out transform hover:-translate-y-0.5 hover:shadow-lg*/}
                                {/*       active:scale-95 text-center"*/}
                                {/*>*/}
                                {/*    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">*/}
                                {/*        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>*/}
                                {/*    </svg>*/}
                                {/*    Water Quality*/}
                                {/*</a>*/}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {peripheralData.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                            <input
                                type="text"
                                placeholder={t('search_peripherals') || 'Search peripherals...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">{t('items_per_page') || 'Items per page: '}</span>
                            <select
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                className="px-3 py-2 border border-gray-300 rounded-lg"
                            >
                                {itemsPerPageOptions.map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={exportToCSV}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            {t('export_csv') || 'Export CSV'}
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                {filteredData.length > 0 ? (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th 
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('name')}
                                        >
                                            <div className="flex items-center gap-1">
                                                {t('peripheral_name') || 'Name'}
                                                {sortField === 'name' && (
                                                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('date')}
                                        >
                                            <div className="flex items-center gap-1">
                                                {t('date') || 'Date'}
                                                {sortField === 'date' && (
                                                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('total_duration')}
                                        >
                                            <div className="flex items-center gap-1">
                                                {t('total_duration') || 'Total Duration'}
                                                {sortField === 'total_duration' && (
                                                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('on_count')}
                                        >
                                            <div className="flex items-center gap-1">
                                                {t('on_count') || 'On Count'}
                                                {sortField === 'on_count' && (
                                                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('off_count')}
                                        >
                                            <div className="flex items-center gap-1">
                                                {t('off_count') || 'Off Count'}
                                                {sortField === 'off_count' && (
                                                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('first_activity') || 'First Activity'}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('last_activity') || 'Last Activity'}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredData.map((peripheral, index) => (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg">🔌</span>
                                                    <span className="font-medium text-gray-900">{peripheral.peripheral_id?.name}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900 font-medium">
                                                    {new Date(peripheral.date).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className="text-lg font-bold text-blue-600">
                                                        {(peripheral.total_duration / 3600).toFixed(1)}h
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {Math.floor(peripheral.total_duration / 60)}m
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                                                    <span className="text-lg font-bold text-green-600">{peripheral.on_count}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                                                    <span className="text-lg font-bold text-red-600">{peripheral.off_count}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {peripheral.first_activity ? (
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">
                                                            {new Date(peripheral.first_activity).toLocaleDateString()}
                                                        </span>
                                                        <span className="text-xs">
                                                            {new Date(peripheral.first_activity).toLocaleTimeString('en-US', {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span>N/A</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {peripheral.last_activity ? (
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">
                                                            {new Date(peripheral.last_activity).toLocaleDateString()}
                                                        </span>
                                                        <span className="text-xs">
                                                            {new Date(peripheral.last_activity).toLocaleTimeString('en-US', {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span>N/A</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <div>
                                        <p className="text-sm text-gray-700">
                                            {t('page')} <span className="font-medium">{pagination.currentPage}</span> of{' '}
                                            <span className="font-medium">{pagination.totalPages}</span>
                                            {' '}({pagination.total} {t('total')} {t('results')})
                                            {searchTerm && <span> (filtered)</span>}
                                        </p>
                                    </div>
                                    
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                            disabled={pagination.currentPage === 1 || loading}
                                            className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span className="sr-only">Previous</span>
                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            {t('previous')}
                                        </button>

                                        <div className="hidden sm:flex space-x-1">
                                            {getPaginationNumbers().map((page, index) => {
                                                if (page === '...') {
                                                    return (
                                                        <span
                                                            key={`ellipsis-${index}`}
                                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                                                        >
                                                            ...
                                                        </span>
                                                    );
                                                }
                                                
                                                const isCurrentPage = page === pagination.currentPage;
                                                return (
                                                    <button
                                                        key={page}
                                                        onClick={() => handlePageChange(page)}
                                                        disabled={loading}
                                                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors ${
                                                            isCurrentPage
                                                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                    >
                                                        {page}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <div className="sm:hidden">
                                            <span className="text-sm text-gray-700">
                                                {t('page')} {pagination.currentPage} of {pagination.totalPages}
                                            </span>
                                        </div>

                                        <button
                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                            disabled={pagination.currentPage === pagination.totalPages || loading}
                                            className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {t('next')}
                                            <span className="sr-only">Next</span>
                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-12">
                        <FontAwesomeIcon icon={faSnowplow} className="text-6xl text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('no_data')}</h3>

                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPeripheralStatusReportPage;